import type React from 'react';
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  BadgeCheck, Calendar, ExternalLink, Globe, Briefcase,
  Hand, Handshake, Ban, Users,
} from 'lucide-react';
import serviceProviderApi, { type ServiceProviderRecord } from '@showliner/shared/services/serviceProviderApi';
import { connectionApi } from '@showliner/shared/services/connectionApi';
import { useUser } from '@showliner/shared/context/UserContext';
import { useManagedEntity } from '@showliner/shared/context/ManagedEntityContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { getAssetUrl, getAdminUrl } from '@/utils/crossAppLinks';
import {
  renderSocialIcon, getPlatformColor, type SocialPlatformKey,
  getPlatformLabel, type SocialLinkEntry, getVisibleSocialLinks,
} from '@showliner/shared/utils/socialMedia';
import type { ProfileLink } from '@showliner/shared/types/profileLinks';
import { toast } from 'sonner';
import { usePageTitle } from '@/hooks/usePageTitle';
import ProfileContact, { type PublicContact } from '@/components/ProfileContact';

// --- Social icon link (identical across profile types) ---

const SocialIconLink: React.FC<{ platform: string; url: string }> = ({ platform, url }) => {
  const platformKey = platform.toLowerCase() as SocialPlatformKey;
  const icon = renderSocialIcon(platformKey, { size: 20 });
  const color = getPlatformColor(platformKey);

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={getPlatformLabel(platform)}
      className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-border/50 hover:scale-110 transition-all duration-200"
      style={{ color }}
    >
      {icon || <Globe className="h-5 w-5" />}
    </a>
  );
};

const ProfileLinkButton: React.FC<{ title: string; url: string }> = ({ title, url }) => (
  <a
    href={url}
    target="_blank"
    rel="noopener noreferrer"
    className="w-full flex items-center justify-between px-5 py-3.5 rounded-lg border border-border/50 bg-white hover:border-primary/30 hover:shadow-sm transition-all duration-200 group"
  >
    <span className="text-sm font-medium text-grey-900">{title}</span>
    <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
  </a>
);

const ProfileSkeleton: React.FC = () => (
  <div className="flex flex-col items-center px-4 pt-12 pb-8 max-w-lg mx-auto">
    <Skeleton className="h-28 w-28 rounded-full" />
    <Skeleton className="h-7 w-48 mt-4" />
    <Skeleton className="h-4 w-64 mt-2" />
    <div className="flex gap-3 mt-6">
      {[1, 2, 3].map(i => <Skeleton key={i} className="h-10 w-10 rounded-full" />)}
    </div>
    <div className="w-full space-y-3 mt-8">
      {[1, 2, 3].map(i => <Skeleton key={i} className="h-14 w-full rounded-lg" />)}
    </div>
  </div>
);

/**
 * Public profile for service providers (promoters, photographers,
 * sound engineers, videographers, etc.). Structurally mirrors the
 * performer profile — avatar + name + specialty + bio + social +
 * custom links — with `specialty` replacing `genres` as the free-text
 * role descriptor. Collaboration (connection request) works the same.
 */
const ServiceProviderProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useUser();
  const { currentEntity: managedEntity } = useManagedEntity();

  const [provider, setProvider] = useState<(ServiceProviderRecord & { publicContact?: PublicContact | null }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCollaborating, setIsCollaborating] = useState(false);
  const [isCollaborator, setIsCollaborator] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  usePageTitle(provider?.name);

  useEffect(() => {
    if (!id) return;
    const fetchProvider = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await serviceProviderApi.getServiceProvider(id);
        setProvider(data);

        // Connection status (same mechanism as performer profile).
        if (managedEntity && data.managedEntityId) {
          try {
            const status = await connectionApi.getConnection(managedEntity.id, data.managedEntityId);
            setIsCollaborating(status?.status === 'accepted' || status?.status === 'pending');
            const hasBidirectional = await connectionApi.areConnected(managedEntity.id, data.managedEntityId);
            setIsCollaborator(hasBidirectional);
          } catch {
            setIsCollaborator(false);
          }
        }
      } catch {
        setError('Couldn\'t load this profile.');
      } finally {
        setLoading(false);
      }
    };
    fetchProvider();
  }, [id, managedEntity]);

  const handleCollaboration = async () => {
    if (!managedEntity || !provider?.managedEntityId) {
      toast.error('Please sign in to collaborate');
      return;
    }
    try {
      if (isCollaborating) {
        await connectionApi.disconnect(managedEntity.id, provider.managedEntityId);
        setIsCollaborating(false);
        setIsCollaborator(false);
        toast.success('Collaboration removed');
      } else {
        await connectionApi.requestConnection(managedEntity.id, provider.managedEntityId);
        setIsCollaborating(true);
        toast.success('Collaboration request sent');
      }
    } catch {
      toast.error('That didn\'t go through. Try again.');
    }
  };

  if (loading) return <ProfileSkeleton />;

  if (error || !provider) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Briefcase className="h-16 w-16 text-muted-foreground/50" />
        <p className="text-lg text-muted-foreground">{error || 'Profile not found'}</p>
        <Button variant="outline" asChild>
          <Link to="/">Back home</Link>
        </Button>
      </div>
    );
  }

  const isLocal = managedEntity && provider.parentEntityId === managedEntity.id;
  const visibleSocialLinks = getVisibleSocialLinks((provider.socialLinks as SocialLinkEntry[]) || []);
  const hasSocialLinks = visibleSocialLinks.length > 0;
  const customLinks = ((provider.customLinks as ProfileLink[]) || [])
    .filter(l => l.visible !== false)
    .sort((a, b) => a.position - b.position);

  return (
    <div className="flex flex-col items-center px-4 pt-12 pb-16 max-w-lg mx-auto">
      {/* Avatar — circular like performer; service providers are people/
          small teams, not places, so circular feels right. */}
      <Avatar className="h-28 w-28 shadow-lg">
        {provider.profilePicture ? (
          <AvatarImage src={getAssetUrl(provider.profilePicture)} alt={provider.name} />
        ) : null}
        <AvatarFallback className="text-3xl font-bold bg-primary text-white">
          {provider.name.charAt(0).toUpperCase()}
        </AvatarFallback>
      </Avatar>

      <div className="flex items-center gap-2 mt-4">
        <h1 className="font-display text-2xl font-bold tracking-tight text-grey-900">{provider.name}</h1>
        {provider.isVerified && <BadgeCheck className="h-6 w-6 text-verified" />}
      </div>

      {isLocal && (
        <Badge variant="outline" className="mt-1.5" title="Not on Showliner yet">
          <Users className="mr-1 h-3 w-3" /> Unclaimed
        </Badge>
      )}

      {/* Specialty — single free-text chip (Promoter, Photographer, etc.) */}
      {provider.specialty && (
        <div className="flex justify-center mt-3">
          <Badge variant="secondary" className="text-xs">
            <Briefcase className="mr-1 h-3 w-3" /> {provider.specialty}
          </Badge>
        </div>
      )}

      {provider.bio && (
        <p className="text-sm text-muted-foreground text-center mt-3 max-w-md leading-relaxed">
          {provider.bio}
        </p>
      )}

      {hasSocialLinks && (
        <div className="flex flex-wrap justify-center gap-2.5 mt-5">
          {visibleSocialLinks.map((entry) => (
            <SocialIconLink key={entry.platform} platform={entry.platform} url={entry.url} />
          ))}
        </div>
      )}

      {/* Public contact tier, when the workspace published one */}
      <ProfileContact contact={provider.publicContact} />

      <div className="flex items-center gap-2 mt-5">
        {!isLocal && managedEntity && provider.managedEntityId && (
          <Button
            variant={isCollaborating ? 'outline' : 'default'}
            size="sm"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            onClick={handleCollaboration}
          >
            {isHovering && isCollaborating ? (
              <><Ban className="mr-1 h-3.5 w-3.5" /> Remove</>
            ) : isCollaborating ? (
              <><Handshake className="mr-1 h-3.5 w-3.5" /> {isCollaborator ? 'Collaborator' : 'Pending'}</>
            ) : (
              <><Hand className="mr-1 h-3.5 w-3.5" /> Collaborate</>
            )}
          </Button>
        )}
        {managedEntity && (
          <Button variant="outline" size="sm" asChild>
            <a href={getAdminUrl('/events/create')}>
              <Calendar className="mr-1 h-3.5 w-3.5" /> Create event
            </a>
          </Button>
        )}
        {!user && (
          <Button size="sm" asChild>
            <Link to="/register">Sign up to collaborate</Link>
          </Button>
        )}
      </div>

      {/* Custom profile blocks (URL links, etc.) */}
      <div className="w-full space-y-3 mt-8">
        {customLinks.map((link) => {
          if (link.type === 'url') {
            return <ProfileLinkButton key={link.id} title={link.title} url={link.url} />;
          }
          return null;
        })}
      </div>
    </div>
  );
};

export default ServiceProviderProfile;
